import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { Divider, TextField, Typography } from '@material-ui/core';
import SettingsInputCompositeOutlinedIcon from '@material-ui/icons/SettingsInputCompositeOutlined';
import { Autocomplete } from '@material-ui/lab';

import Card from '../../../common/card/Card';
import CardContent from '../../../common/card/CardContent';
import CardTitle from '../../../common/card/CardTitle';
import {
  useFindProductReleases,
  useUpdateProductDesignInput,
  useUpdateProductDesignOutput,
  useUpdateProductDesignSpec,
  useUpdateProductUserNeed,
  useUpdateProductValidation,
  useUpdateProductVerification
} from '../Product.hooks';
import {
  ProductComponent,
  ProductDesignInput,
  ProductDesignOutput,
  ProductRelease,
  ProductUserNeed,
  ProductValidation,
  ProductVerification
} from '../Product.interface';
import { LocalShipping } from '@material-ui/icons';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';

const StyledCard = styled(Card)`
  .MuiSelect-icon {
    display: none;
  }

  .MuiDivider-light {
    margin-top: 10px;
  }

  .title {
    height: 16px;
    line-height: 14.06px;
    font-size: 12px;
    font-weight: bold;
  }

  .MuiInput-root {
    border: 1px solid rgb(204, 204, 204);
    width: 100%;
    border-radius: 8px;
    padding: 2px 8px;
  }

  .MuiInput-input {
    background-color: white;
  }

  .MuiAutocomplete-tag {
    margin: 2px;
    background-color: #f0f1f7;
  }
  .MuiAutocomplete-endAdornment {
    display: none;
  }
`;

const ProductsSubSectionReleaseCard: React.FC<{
  productId: number;
  productSubSection:
    | ProductUserNeed
    | ProductDesignInput
    | ProductDesignOutput
    | ProductVerification
    | ProductValidation;
  disabled?: boolean;
  // release override for versioning display; display empty field if null
  readOnlyRelease?: Partial<ProductRelease> | null;
}> = ({ productSubSection, productId, disabled, readOnlyRelease }) => {
  const history = useHistory();

  const [release, setRelease] = useState<Partial<ProductRelease> | null>(
    readOnlyRelease || productSubSection?.release || null
  );
  useEffect(() => {
    if (readOnlyRelease || readOnlyRelease === null) {
      setRelease(readOnlyRelease);
    } else if (productSubSection?.release) {
      setRelease(productSubSection.release);
    } else {
      setRelease(null);
    }
  }, [readOnlyRelease, productSubSection]);

  const { updateProductUserNeed } = useUpdateProductUserNeed(
    productId,
    productSubSection.id || 0
  );

  const { updateProductValidation } = useUpdateProductValidation(
    productId,
    productSubSection.id
  );

  const { updateProductDesignInput } = useUpdateProductDesignInput(
    productId,
    productSubSection.id
  );

  const { updateProductDesignOutput } = useUpdateProductDesignOutput(
    productId,
    productSubSection.id
  );

  const { updateProductDesignSpec } = useUpdateProductDesignSpec(
    productId,
    productSubSection.id
  );

  const { updateProductVerification } = useUpdateProductVerification(
    productId,
    productSubSection.id
  );

  const { productReleases } = useFindProductReleases(productId, true);
  const { isFieldDisabled } = useTaskEntityFields(productSubSection.task);
  const hasReleaseConfigured = productReleases && productReleases.length;
  return (
    <StyledCard>
      <CardTitle>
        <LocalShipping />
        Release
      </CardTitle>
      <Divider light />
      <CardContent padding='medium'>
        <Typography className='title'>Assign Release</Typography>
        <Autocomplete
          className='auto-complete'
          fullWidth
          disabled={disabled || isFieldDisabled('release')}
          filterSelectedOptions
          value={release}
          options={productReleases || []}
          getOptionLabel={option => option.name || ''}
          onChange={(e, value) => {
            const delta = {
              id: productSubSection.id,
              release: value as ProductRelease
            };
            productSubSection.release = delta.release;
            setRelease(value);
            history.location.pathname.split('/')[4] === 'validations'
              ? updateProductValidation(delta)
              : history.location.pathname.split('/')[4] === 'design-inputs'
              ? updateProductDesignInput(delta)
              : history.location.pathname.split('/')[4] === 'design-outputs'
              ? updateProductDesignOutput(delta)
              : history.location.pathname.split('/')[4] === 'user-needs'
              ? updateProductUserNeed(delta)
              : history.location.pathname.split('/')[4] === 'design-specs'
              ? updateProductDesignSpec(delta)
              : history.location.pathname.split('/')[4] === 'verifications'
              ? updateProductVerification(delta)
              : (() => {})(); // do nothing
          }}
          renderInput={params => (
            <TextField
              {...params}
              fullWidth
              placeholder='Assign a release'
              variant='outlined'
              helperText={
                !hasReleaseConfigured && (
                  <em>
                    No releases defined for product, please go to the{' '}
                    <Link to={{ pathname: `/portal/products/${productId}` }}>
                      product settings page
                    </Link>{' '}
                    to add releases.
                  </em>
                )
              }
            />
          )}
        />
      </CardContent>
    </StyledCard>
  );
};

export default ProductsSubSectionReleaseCard;
